'use client';

/**
 * Global Plans (subscribe) dialog host.
 *
 * Plan-gate 403s (`upgrade_required` for signed-in users, e.g. the per-plan
 * workspace cap) used to route to the wallet page, burying the actual
 * upgrade action two clicks deep. Any call site can now open the Plans
 * dialog directly via {@link openPlansDialog} — the host is mounted once in
 * AppProviders and renders {@link SubscribeDialog} (which portals to
 * document.body, so it works from any surface).
 *
 * Distinct from `lib/api/upgrade-required.ts`: that event is the GUEST
 * economy gate and opens the signup dialog. This one is for signed-in
 * users who need a bigger plan.
 */

import { useEffect, useState } from 'react';
import { SubscribeDialog } from '@/components/subscribe-dialog';

export const OPEN_PLANS_EVENT = 'robutler:open-plans';

/** Open the Plans dialog from anywhere (no-op during SSR). */
export function openPlansDialog(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_PLANS_EVENT));
}

export function PlansDialogHost() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_PLANS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PLANS_EVENT, onOpen);
  }, []);

  if (!open) return null;
  return <SubscribeDialog open onClose={() => setOpen(false)} />;
}
